// Product changelog shown on the Dashboard ("What's new").
//
// Add a NEW entry at the TOP after every major push. Write in plain,
// user-facing language (what changed for the team), not commit-speak.
// tag ∈ 'performance' | 'feature' | 'reliability' | 'fix'
export const CHANGELOG = [
  {
    date: '2026-10-06',
    tag: 'reliability',
    title: 'Safer publishing, fixed shipping weights, no more duplicate rules',
    items: [
      'Fixed the shipping weight in the Switzerland (DE/FR) and Canada (FR) feeds, which showed “\\1.\\2KILOGRAMS” instead of the weight.',
      'A feed is no longer published when it suddenly has less than half the products of its last good run (e.g. a source came back empty) — the previous file stays live and the run is marked as an error with the reason. Likewise a source sync that returns less than half its products is cancelled instead of switching everything off.',
      'Two generations of the same feed (e.g. a manual Generate during the nightly run) can no longer corrupt the published file, and the nightly run waits for a manual one to finish.',
      'Saving rules, mappings or master rules twice no longer creates duplicates, and failed saves now show an error message instead of silently doing nothing.',
      'Unsaved edits survive switching between the General / Mapping / Rules / Variables tabs, and switching between feeds no longer shows the previous feed’s preview or excluded list.',
    ],
  },
  {
    date: '2026-09-25',
    tag: 'fix',
    title: 'Google feeds: cleaner titles, prices, shipping & energy labels',
    items: [
      'Variant titles no longer lose their colour/size when the variant name happens to contain “no” (Noir, Noyer, Walnoot…): ~15k French and ~3k Dutch/German variants were showing duplicate titles. Translated “Default Titel / Standaardtitel” no longer leaks into titles.',
      'A sale price is only sent when it’s a real discount; when it isn’t, it becomes the regular price (what the product page charges).',
      'The energy efficiency class is only sent where Google still accepts it (Switzerland, Norway, UK) and only as a valid class — the EU moved to EPREL certification in 2025.',
      'Items without a shipping price no longer send an incomplete shipping block (Google rejects those); they fall back to the Merchant Center shipping settings and show a “No shipping price” warning in the quality report.',
    ],
  },
  {
    date: '2026-07-06',
    tag: 'feature',
    title: 'Feed refresh times on the dashboard',
    items: [
      'Each feed in the dashboard’s Feeds panel now shows when it was last refreshed (e.g. “↻ 3h ago”) — green when it’s current, amber when it hasn’t been refreshed since the last scheduled run. Hover for the exact timestamp.',
      'US, MX & CA feeds get a second daily refresh (07:00) matched to their market timezone, on top of the overnight run — driven by the market timezones in Pricython, so far-timezone markets join automatically.',
    ],
  },
  {
    date: '2026-07-02',
    tag: 'feature',
    title: 'Colour & variant title now localized from Shopify',
    items: [
      'The product colour and the variant title (e.g. "… Kushi 33 | Rame") are now pulled per language from Shopify’s option translations, so the Italian/Polish/Portuguese feeds show them in the right language (Rame / Miedź / Cobre) instead of English or nothing — matching the German feed. German/French/Dutch/Spanish are unchanged, just sourced dynamically.',
      'Removed the “N rule groups” chip from the Feeds list (it drew the eye without adding much); the assigned groups are still shown on each feed’s editor page.',
    ],
  },
  {
    date: '2026-07-02',
    tag: 'feature',
    title: 'Italian, Polish & Portuguese feeds',
    items: [
      'New localized feeds for Italy, Poland and Portugal — titles, descriptions and highlights come from the Shopify translations (which existed but were never being pulled in; the sync now requests it/pl/pt too), and the structured product specs are localized in-app. Prices/costs stay in the local currency (PLN for Poland; EUR for Italy/Portugal).',
      'The Feeds list country grouping is now a light label + divider instead of a filled grey band, so the table reads as one continuous list.',
    ],
  },
  {
    date: '2026-07-02',
    tag: 'feature',
    title: 'Sidebar grouped by pipeline stage',
    items: [
      'The left menu is now grouped to match the data flow: Dashboard and Feeds stay at the top, then a “Catalog” section (Sources, Master rules, Categorisation) and a “Config” section (Channels, Runs). Easier to find where a setting lives.',
      '“Schedule” is renamed to “Runs” — the page is the pipeline run history (the actual schedule lives in the server cron), so the name now says what it is. The ⌘K command palette also gained the pages it was missing (Master rules, Categorisation, Runs).',
    ],
  },
  {
    date: '2026-07-02',
    tag: 'feature',
    title: 'Cost of goods sold on every feed, converted to the local currency',
    items: [
      'cost_of_goods_sold is now mapped on all 42 feeds (it existed on only 12, and the non-EUR ones sent the EUR amount mislabelled in the local currency). EUR markets emit the cost as-is; the 26 non-EUR feeds convert it with the same exchange rates Pricython uses for pricing — live ECB rates, with the fixed rates for pegged currencies (AED, SAR, TWD, EGP) — so cost and price in a feed are always in the same, correct currency.',
      'Rates are re-baked automatically at the start of every nightly run (each feed has a visible “COGS: EUR → …” rule holding the current rate). Expect Google Merchant profit reporting for non-EUR markets to become accurate — and the Channable comparison to show a cost_of_goods_sold mismatch on the 8 feeds where Channable still sends unconverted values.',
    ],
  },
  {
    date: '2026-07-02',
    tag: 'performance',
    title: 'Feed generation ~5× faster',
    items: [
      'The nightly generate phase drops from ~1 minute per feed to ~10 seconds. Four fixes: the “why was this product excluded” bookkeeping now loads only the dropped products instead of re-reading the whole enrichment catalog per feed; feeds that share the same master rules and language now share one rules pass instead of each repeating it; the per-product quality checks run as fast columnar operations instead of one product at a time; and manual “Generate selected” from the UI now uses the same shared caches as the nightly run.',
      'Old run reports are also slimmed down automatically (the drill-down record lists are kept for each feed’s three newest runs), so the shared database stops growing by ~55 MB a night. The output feeds are byte-for-byte identical to before.',
    ],
  },
  {
    date: '2026-07-01',
    tag: 'fix',
    title: 'Energy class translated to the feed language',
    items: [
      'Some energy_efficiency_class values are English phrases ("A+ to E depending on light source", "A to G") rather than a bare class letter, so they showed English in the DE/FR/ES/NL feeds. Those phrases now translate to the feed language (e.g. DE "A+ bis E je nach Leuchtmittel", FR "A+ à E selon la source lumineuse"); the class letters and the English feeds are unchanged.',
    ],
  },
  {
    date: '2026-07-01',
    tag: 'feature',
    title: 'Search the excluded products',
    items: [
      'The Excluded tab now has a search box — filter the excluded products by title or id, alongside the by-reason filter. Quick way to check whether a specific product dropped out and why.',
    ],
  },
  {
    date: '2026-07-01',
    tag: 'reliability',
    title: 'Dashboard “no run today” fixed',
    items: [
      'The dashboard health tiles judged “ran today” against 05:00, but the nightly finishes earlier (small hours), so after 05:00 it wrongly showed “No run today”, 0/42 feeds and 0/7 sources even though everything had run. Now it checks against the local day with a pre-dawn grace window.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'fix',
    title: 'Full-price products no longer dropped from country feeds',
    items: [
      'Across all 30 country feeds, when a product has a selling price but no compare-at (“was”) price — i.e. it’s simply not on sale — the feed now falls back to the selling price for the compare-at, instead of dropping the product as “Only <country>-priced”. Products with no price for that country at all are still excluded. Fixes full-price / Second-Chance items disappearing from Spain and the other markets.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'reliability',
    title: 'Single-feed “Generate” now uses the fast engine',
    items: [
      'The per-feed “Generate” button now runs the same fast vectorized (Polars) engine as the nightly batch, instead of the slower row-by-row path that held a database cursor open for minutes and could freeze on the shared database (the “stuck at N / 43,423 rows” problem). It’s much faster and can’t stall that way. Live progress is now a stage indicator (Planning → Loading → Generating → Done) rather than a row counter, since the fast engine works in bulk.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: '“Run pipeline” now asks to confirm',
    items: [
      '“Run pipeline” kicks off a full sync of every source plus a rebuild of all feeds — so it now shows a confirmation first, to stop the reflex click. The dialog also points you to a single feed’s Generate / Preview when you just want to test one feed rather than run everything.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'reliability',
    title: 'Manual “Generate” can’t hang forever',
    items: [
      'A manual single-feed generate now has a time budget (15 min), so a run that stalls on the shared database self-cancels and reports a failure instead of sitting at a frozen row count indefinitely. Combined with the stuck-run reaper, a feed can always be re-run.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Auto fields shown in the mapping (read-only)',
    items: [
      'The mapping editor now also lists the auto-generated fields (product_detail, identifier_exists) in a read-only “Auto-generated” section — with an auto icon and a lock so it’s clear they’re not editable — plus an info button that expands a plain-language explanation of how each one is generated (the category-driven specs, or the no-GTIN/MPN rule). So they’re visible as part of the feed without being mis-mappable.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Auto fields shown in the preview & Channels reference',
    items: [
      'The auto-generated feed fields — product_detail (structured specs) and identifier_exists — now appear as columns in the feed Preview table (and in the row detail), instead of only in the raw XML. They’re also listed on the Channels reference page, tagged “auto” because they’re generated at export time, so they’re no longer offered as mapping targets (you can’t map a derived field).',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Fuller raw feed preview',
    items: [
      'The feed preview’s “Raw” view now shows a few complete items (was cut off at 5k characters), so the blocks that sit at the end of each item — product_detail, identifier_exists, the nested shipping group — are actually visible instead of chopped off. Note these are XML-export blocks, so they show in the Raw view, not the Table view (which only lists mapped columns).',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'reliability',
    title: 'Feeds no longer get stuck “running”',
    items: [
      'A single-feed “Generate” whose process was killed mid-run (out-of-memory, container restart, deploy) used to leave the feed flagged “running” forever — the auto-cleanup only covered full pipeline runs, not one-off generations. The cleanup now also reaps these orphaned single-feed runs and clears the stuck flag (while leaving any generation that’s genuinely still in progress alone), so a feed can always be re-run. Added a reap_stale_feedruns command to clear a backlog on demand.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Product highlights now in the feed language',
    items: [
      'product_highlight (the marketing blurb + perks Google shows as highlights — e.g. “…artisan glass beauty, customizable design ✔ Free shipping ✔ 30 days trial”) now appears in the feed’s own language instead of always English. The German, Spanish and French feeds pull the translated version straight from Shopify (ES “…belleza en vidrio artesanal… ✔ Envío gratis ✔ 30 días de prueba”, DE “…handwerkliche Glasschönheit… ✔ Kostenloser Versand…”, FR “…beauté en verre artisanal… ✔ Livraison gratuite…”). English feeds are unchanged.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Structured product specs in the feed (product_detail)',
    items: [
      'Feeds now emit structured product_detail specs — category-driven (bulbs → wattage / lumens / colour temperature / socket; fixtures → IP / material / socket / dimensions; …), each value taken from the most specific source available (variant → product → Shopify metafield), with the labels in the feed’s language (“Especificaciones”, “Spezifikationen”, …). Plus identifier_exists=no for products with no GTIN/MPN, so Google stops the missing-identifier penalty.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'Excluded products tab',
    items: [
      'Each feed now has an “Excluded” tab showing the PIM products that did NOT make it into the feed and exactly why — broken down by reason (e.g. “Remove variants out of production”, “Only ES-priced”, brand exclusions). Click a reason to filter, click a product to inspect. Recorded during generation so it opens instantly.',
    ],
  },
  {
    date: '2026-06-30',
    tag: 'feature',
    title: 'More Merchant Center quality checks',
    items: [
      'The Quality tab now catches more of what Google disapproves, before it ships: sale price higher than the price, invalid GTIN check digit, promotional or all-uppercase titles, variants with no differentiating attribute, shipping cost above the item price, duplicate ids, the now-deprecated energy_efficiency_class, and copy that may trip Google’s sensitive-category classifiers (adult / healthcare — usually false positives on lighting wording like “Nude” or “wellness”).',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'performance',
    title: 'Instant quality drill-down',
    items: [
      'Clicking a quality issue to see the affected records is now instant (was ~100s — it used to regenerate the whole feed each time). The records are captured during generation and read back immediately; feeds generated before this update fall back to the slower recompute until their next run.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'Localized feed content straight from Shopify',
    items: [
      'Translated titles & descriptions now come directly from Shopify’s translations (per language), instead of the PIM. Coverage for the live catalog is the same (ES/DE ≈ 100%), and adding a new language is now just one config entry — no schema or PIM change. Verified end-to-end: ES shows “…Cord Lámpara”, DE “…Cord Leuchte”.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'Preview which items a master rule affects',
    items: [
      'Each master rule now has an “Affected items” button (in the rule editor and next to the rule in the list). It scans the whole catalog with the same merged base + enrichment context the feeds use, and shows how many items match the rule’s condition plus a thumbnail grid of examples — including the value the rule would set (e.g. the Google category). Makes it easy to sanity-check a rule like “Speakers” before it ships.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'New product-category engine (40 categories)',
    items: [
      'Google product category is now a 40-rule cascade driven by collections + tags + product type + title (every target validated against the official Google taxonomy), replacing the 12 hand-written rules. Much more granular — Chandeliers, Pathway lighting, furniture (chairs / stools / tables / shelving / sofas), decor (vases / rugs / mirrors / cushions), kitchen & dining (plates / mugs / glassware), and more.',
      'The rules live in their own editable “Categorisation” master-rule group, and the Categorisation page explains them in priority order.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'reliability',
    title: 'Live market pricing (no more daily CSV lag)',
    items: [
      'The Pricing Markets source now reads directly from the live pricing table instead of a once-a-day CSV export, so price/compare-at changes show up in the feeds without the export lag.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'Second Chance products marked “refurbished”',
    items: [
      'Feeds now set g:condition to “refurbished” for clearance / Second-Chance products, matching how Perfy identifies them — “second chance / seconde chance / tweede kans / 2nd chance / showroom” in the title or tags, excluding “showroom configuration” display setups. Everything else stays “new”.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'Mapping fields follow the Google XML order',
    items: [
      'The mapping editor now lists fields in the canonical Google Shopping order (required → identifiers → categorisation → media → pricing → shipping → attributes → custom labels) — the same order the generated XML uses.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'performance',
    title: 'Catalog merged once per run, not per feed',
    items: [
      'The nightly run now loads and merges the product catalog + all enrichment sources a single time and shares it across every feed, instead of rebuilding it for each of the 41 feeds.',
      'The load/merge phase is ~30× faster across the batch (≈19 min → ≈1 min). Output is byte-for-byte identical to before — verified on AE, UK, FR and DE.',
    ],
  },
  {
    date: '2026-06-29',
    tag: 'feature',
    title: 'Categorisation page',
    items: [
      'New Categorisation page explains how each product gets its Google category — Shopify collections → master rules (top-to-bottom, last match wins) → taxonomy ID — and lists the live rules in priority order.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'performance',
    title: 'New vectorized (Polars) feed engine',
    items: [
      'Feed generation now runs on a columnar Polars engine instead of a row-by-row loop — the per-feed transform is ~5× faster and uses a fraction of the memory.',
      'Output is validated byte-for-byte identical to before (AE, BE, FR, UK, DE — 100% per-field match), and the full nightly run drops from ~20 min toward a few minutes.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'feature',
    title: 'Per-source & per-feed run history',
    items: [
      'Every source sync and feed generation is now recorded. Open the history (clock icon) on any source or feed to see recent runs with status, duration, and counts.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'feature',
    title: 'Run on demand + run history',
    items: [
      'A “Run pipeline” button (top-right) starts a sync + generate — or generate-only — without waiting for the nightly schedule.',
      'New Schedule page lists recent runs with status, duration, and a per-source / per-feed breakdown of what passed or failed.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'performance',
    title: 'Feeds generate ~5× faster',
    items: [
      'Fixed a slow data-loading step — big country feeds now build in ~30–60s instead of ~6 min.',
      'Shopify sync now uses the Bulk API, with no per-product variant limits.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'feature',
    title: 'Live status — no more refreshing',
    items: [
      'Sources, Feeds and the Dashboard update on their own while a sync or generation is running.',
      'A pulsing “live” badge shows when a page is auto-updating.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'reliability',
    title: 'More reliable nightly run',
    items: [
      'Pipeline runs can no longer pile up on top of each other and slow each other down.',
      'A slow or contended feed is cancelled on a time budget instead of stalling the whole run; the run also uses far less memory.',
      'Interrupted runs are cleaned up automatically.',
      '“Last sync” now shows when a sync finished, not when it started.',
    ],
  },
  {
    date: '2026-06-26',
    tag: 'feature',
    title: 'Dashboard & Feeds upgrade',
    items: [
      'Dashboard shows health at a glance — last run, feeds & sources up to date (24h), and any errors — plus this “What’s new” panel (also in the top-right bell).',
      'Feeds: bulk-select and generate in one go, an active/disabled filter, and disabled feeds clearly marked as “won’t run” with a quick enable/disable toggle.',
      'Paginated feeds list and a refreshed sign-in screen with the Feedy logo.',
    ],
  },
  {
    date: '2026-06-25',
    tag: 'feature',
    title: 'Stable feed URLs + shared database',
    items: [
      'Feeds are mirrored to Google Cloud Storage for stable, always-on URLs.',
      'Feedy now runs on the shared Postgres database (the same one as the PIM).',
    ],
  },
];

export default CHANGELOG;
